/**
 * Permissions utility — defines modules, role defaults, and
 * the resolver `hasPermission(user, moduleKey, action)`.
 *
 * Per-user `user.permissions` (when present) overrides role defaults.
 * Otherwise we fall back to the role-based defaults below.
 */

// Available modules grouped for the UI
export const PERMISSION_MODULE_GROUPS = [
  {
    label: 'Main',
    modules: [
      { key: 'dashboard', label: 'Dashboard', icon: '🏠' },
      { key: 'activities', label: 'Activities', icon: '📋' },
      { key: 'customers', label: 'Customers', icon: '👥' },
      { key: 'products', label: 'Products', icon: '📦' },
      { key: 'leads', label: 'Leads', icon: '🎯' },
      { key: 'sales_invoices', label: 'Sales Invoices', icon: '💰' },
      { key: 'delivery_notes', label: 'Delivery Notes', icon: '🚚' },
      { key: 'service_requests', label: 'Service Requests', icon: '🛎️' },
      { key: 'payments', label: 'Customer Payments', icon: '💳' },
      { key: 'supplier_payments', label: 'Supplier Payments', icon: '🏦' },
      { key: 'sales_reports', label: 'Sales Reports', icon: '📈' },
      { key: 'suppliers', label: 'Suppliers', icon: '🏭' },
      { key: 'purchase_orders', label: 'Purchase Orders', icon: '📑' },
      { key: 'purchase_entries', label: 'Purchase Entries', icon: '📦' },
      { key: 'vat_return', label: 'VAT Return', icon: '📊' },
      { key: 'other_purchases', label: 'Other Purchases', icon: '📂' },
      { key: 'daily_tasks', label: 'Daily Tasks', icon: '✅' },
      { key: 'attendance', label: 'Attendance (Self Punch)', icon: '🕒' },
      { key: 'attendance_report', label: 'Attendance Report', icon: '🗓️' },
    ],
  },
  {
    label: 'Admin',
    modules: [
      { key: 'users', label: 'Users', icon: '👤' },
      { key: 'teams', label: 'Teams', icon: '👥' },
      { key: 'master_data', label: 'Master Data', icon: '⚙️' },
      { key: 'reports', label: 'Reports', icon: '📊' },
      { key: 'stock_report', label: 'Stock Report', icon: '📦' },
      { key: 'company_settings', label: 'Company Settings', icon: '🔧' },
      { key: 'bulk_delete', label: 'Bulk Delete', icon: '🗑️' },
      { key: 'location_tracking', label: 'Location Tracking', icon: '📍' },
      { key: 'approval_settings', label: 'Approval Settings', icon: '✅' },
    ],
  },
];

// Special action keys (not standard CRUD). Currently only `can_approve` which
// authorises a user to be added to the PO/Invoice approval pools.
export const SPECIAL_ACTION_MODULES = [
  { key: 'approvals', label: 'Document Approvals', icon: '🛡️', actions: ['can_approve'] },
];

export const ALL_MODULES = PERMISSION_MODULE_GROUPS.flatMap((g) => g.modules);

export const PERMISSION_ACTIONS = [
  { key: 'view', label: 'View' },
  { key: 'create', label: 'Create' },
  { key: 'edit', label: 'Edit' },
  { key: 'delete', label: 'Delete' },
];

const fullCrud = { view: true, create: true, edit: true, delete: true };
const viewOnly = { view: true, create: false, edit: false, delete: false };
const viewCreateEdit = { view: true, create: true, edit: true, delete: false };

const allModulesFullCrud = () =>
  ALL_MODULES.reduce((acc, m) => ({ ...acc, [m.key]: { ...fullCrud } }), {});

/**
 * Default permission map for each role. Used:
 *  1) As fallback when a user has no custom `permissions` set.
 *  2) To prefill the permissions matrix when admin selects a role
 *     in the user create/edit form.
 */
export const ROLE_DEFAULT_PERMISSIONS = {
  super_admin: allModulesFullCrud(),
  admin: allModulesFullCrud(),
  sales: {
    dashboard: { ...viewOnly },
    activities: { ...viewCreateEdit },
    customers: { ...viewCreateEdit },
    products: { ...viewCreateEdit },
    leads: { ...viewCreateEdit },
    sales_invoices: { ...viewCreateEdit },
    delivery_notes: { ...viewCreateEdit },
    service_requests: { ...viewCreateEdit },
    payments: { ...viewCreateEdit },
    sales_reports: { ...viewOnly },
    stock_report: { ...viewOnly },
    attendance: { ...viewCreateEdit },
  },
  support: {
    dashboard: { ...viewOnly },
    activities: { ...viewCreateEdit },
    customers: { ...viewCreateEdit },
    sales_invoices: { ...viewOnly },
    delivery_notes: { ...viewOnly },
    service_requests: { ...viewCreateEdit },
    payments: { ...viewCreateEdit },
    stock_report: { ...viewOnly },
    attendance: { ...viewCreateEdit },
  },
  data_entry: {
    dashboard: { ...viewOnly },
    activities: { ...viewCreateEdit },
    customers: { ...viewCreateEdit },
    products: { ...viewCreateEdit },
    leads: { ...viewCreateEdit },
    sales_invoices: { ...viewCreateEdit },
    delivery_notes: { ...viewCreateEdit },
    payments: { ...viewCreateEdit },
    sales_reports: { ...viewOnly },
    suppliers: { ...viewCreateEdit },
    purchase_orders: { ...viewCreateEdit },
    purchase_entries: { ...viewCreateEdit },
    vat_return: { ...viewCreateEdit },
    other_purchases: { ...viewCreateEdit },
    stock_report: { ...viewOnly },
    attendance: { ...viewCreateEdit },
    supplier_payments: { ...viewCreateEdit },
  },
  employee: {
    daily_tasks: { ...viewCreateEdit },
    attendance: { ...viewCreateEdit },
  },
};

/**
 * Build a permissions matrix prefilled from a role.
 * Always returns a complete object (every module key present).
 */
export const buildDefaultMatrix = (role) => {
  const base = ROLE_DEFAULT_PERMISSIONS[role] || {};
  return ALL_MODULES.reduce((acc, m) => {
    acc[m.key] = base[m.key]
      ? { ...fullCrud, ...base[m.key] } // ensure all 4 actions present
      : { view: false, create: false, edit: false, delete: false };
    return acc;
  }, {});
};

/**
 * Returns true when the user can perform `action` on `moduleKey`.
 *  - super_admin / admin always pass.
 *  - If user has a non-empty `permissions` map, consult it.
 *  - Otherwise fall back to the role default.
 */
export const hasPermission = (user, moduleKey, action = 'view') => {
  if (!user) return false;
  if (user.role === 'super_admin' || user.role === 'admin') return true;
  const custom =
    user.permissions && Object.keys(user.permissions).length
      ? user.permissions
      : null;
  const defaults = ROLE_DEFAULT_PERMISSIONS[user.role] || {};
  // Prefer the per-user override when set. If the custom map is silent on
  // `moduleKey` (e.g. a new module was added after the user was created),
  // fall back to the role default rather than silently denying access.
  const modulePerms = custom?.[moduleKey] ?? defaults[moduleKey];
  return Boolean(modulePerms?.[action]);
};

/**
 * Returns true when the user is authorised to be an approver (can be picked
 * by admin for PO/Invoice approval pools, and can act on pending docs assigned
 * to them).
 * - admin / super_admin: always true
 * - other roles: only when user.permissions.approvals.can_approve === true
 */
export const canApprove = (user) => {
  if (!user) return false;
  if (user.role === 'super_admin' || user.role === 'admin') return true;
  return Boolean(user?.permissions?.approvals?.can_approve);
};

/**
 * Apply the user's `view_scope` to a list of records.
 *  - 'all'  → user sees every record.
 *  - 'own'  → user sees only records where they are the owner
 *             (checked against `sales_rep_id`, `created_by`, `assigned_to`,
 *             `owner_id`, or `user_id` — whichever the record uses).
 * Admins / super-admins always see all records regardless of view_scope.
 */
export const applyViewScope = (records, user) => {
  if (!user || !Array.isArray(records)) return records || [];
  if (user.role === 'super_admin' || user.role === 'admin') return records;
  const scope = user.view_scope || 'all';
  if (scope !== 'own') return records;
  return records.filter((r) => {
    const ownerId =
      r?.sales_rep_id ||
      r?.created_by ||
      r?.assigned_to ||
      r?.owner_id ||
      r?.user_id;
    return ownerId === user.id;
  });
};
